import {
  filestackUploadSignatureSponsorBooths,
  uploadSignatureFilestack,
  type AssetKindEnum,
  type IntentEnum
} from '@goldcast/api/backend';
import logger from '@goldcast/logger';
import * as filestack from 'filestack-js';

import { FILESTACK_API_KEY } from '@/utils/constants';

export interface FilestackSecurity {
  policy: string;
  signature: string;
}

type FilestackClient = ReturnType<typeof filestack.init>;
type FilestackUploadOptions = Parameters<FilestackClient['upload']>[1];
type FilestackStoreOptions = Parameters<FilestackClient['upload']>[2];

interface UploadFileWithFilestackSecurityRetryArgs {
  file: File;
  uploadOptions: FilestackUploadOptions;
  storeOptions: FilestackStoreOptions;
  filestackIntent: FilestackIntentArgs;
}

export type FilestackIntentArgs =
  | { intent: 'sponsor_booth_asset'; assetKind: AssetKindEnum; event: string; boothId: string }
  | { intent: Extract<IntentEnum, 'organization_asset'>; assetKind: string }
  | { intent: Extract<IntentEnum, 'event_asset'>; assetKind: string; event: string }
  | { intent: Extract<IntentEnum, 'org_filler_slide_asset'>; assetKind: string }
  | { intent: Extract<IntentEnum, 'public_image_event_asset'>; assetKind: string; event: string }
  | { intent: Extract<IntentEnum, 'public_image_org_asset'>; assetKind: string }
  | { intent: Extract<IntentEnum, 'public_image_series_asset'>; assetKind: string; seriesId: string }
  | { intent: Extract<IntentEnum, 'studio_template_asset'>; assetKind: string; templateId: string }
  | { intent: Extract<IntentEnum, 'series_registration_asset'>; assetKind: string; seriesId: string }
  | { intent: Extract<IntentEnum, 'bucket_root_asset'>; assetKind: string };

function buildBody(args: FilestackIntentArgs, sizeBytes?: number): Record<string, string | number> {
  const body: Record<string, string | number> = { intent: args.intent, asset_kind: args.assetKind };
  if (sizeBytes !== undefined) {
    body.size_bytes = sizeBytes;
  }
  if (args.intent === 'event_asset' || args.intent === 'public_image_event_asset') {
    body.event = args.event;
  } else if (args.intent === 'public_image_series_asset' || args.intent === 'series_registration_asset') {
    body.series_id = args.seriesId;
  } else if (args.intent === 'studio_template_asset') {
    body.template_id = args.templateId;
  }
  return body;
}

function resolveSizeHintBytes(sizeBytes?: number): number | undefined {
  if (sizeBytes === undefined || !Number.isFinite(sizeBytes) || sizeBytes <= 0) {
    return undefined;
  }
  return sizeBytes;
}

const FILESTACK_REQUEST_ERROR_TYPE = 'request';
const FILESTACK_AUTH_STATUS_CODES = new Set([401, 403]);
const FILESTACK_SECURITY_ERROR_TOKENS = ['policy', 'signature', 'security', 'expired', 'unauthorized', 'forbidden'];

function getFilestackRequestDetails(error: Record<string, unknown>): Record<string, unknown> | null {
  if (error.type !== FILESTACK_REQUEST_ERROR_TYPE || !error.details || typeof error.details !== 'object') {
    return null;
  }
  return error.details as Record<string, unknown>;
}

function stringifyErrorDetail(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value && typeof value === 'object') {
    return JSON.stringify(value);
  }
  return '';
}

export function isFilestackSecurityError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const maybeError = error as Record<string, unknown>;
  const requestDetails = getFilestackRequestDetails(maybeError);
  if (!requestDetails) {
    return false;
  }

  if (typeof requestDetails.code === 'number' && FILESTACK_AUTH_STATUS_CODES.has(requestDetails.code)) {
    return true;
  }

  const searchableText = [maybeError.message, requestDetails.data].map(stringifyErrorDetail).join(' ').toLowerCase();

  return FILESTACK_SECURITY_ERROR_TOKENS.some(token => searchableText.includes(token));
}

// Deliberately uncached: the expiry is scaled to the size hint, so a reused policy would hand the
// next upload a partially consumed lifetime.
export async function getFilestackSecurity(
  args: FilestackIntentArgs,
  options: { sizeBytes?: number } = {}
): Promise<{ security: FilestackSecurity }> {
  const sizeHintBytes = resolveSizeHintBytes(options.sizeBytes);
  const response =
    args.intent === 'sponsor_booth_asset'
      ? await filestackUploadSignatureSponsorBooths({
          id: args.boothId,
          body: {
            event: args.event,
            asset_kind: args.assetKind,
            ...(sizeHintBytes === undefined ? {} : { size_bytes: sizeHintBytes })
          } as never
        })
      : ((await uploadSignatureFilestack({ body: buildBody(args, sizeHintBytes) as never })) as {
          policy: string;
          signature: string;
        });
  return { security: { policy: response.policy, signature: response.signature } };
}

export async function uploadWithFilestackSecurityRetry<T>(
  args: FilestackIntentArgs,
  upload: (security: FilestackSecurity) => Promise<T>,
  sizeBytes?: number
): Promise<T> {
  let security: FilestackSecurity;
  try {
    ({ security } = await getFilestackSecurity(args, { sizeBytes }));
  } catch (e) {
    logger.error('Failed to fetch Filestack security, upload blocked', e);
    throw e;
  }
  try {
    return await upload(security);
  } catch (error) {
    if (!isFilestackSecurityError(error)) {
      throw error;
    }
    const { security: refreshedSecurity } = await getFilestackSecurity(args, { sizeBytes });
    return upload(refreshedSecurity);
  }
}

export async function uploadFileWithFilestackSecurityRetry({
  file,
  uploadOptions,
  storeOptions,
  filestackIntent
}: UploadFileWithFilestackSecurityRetryArgs): ReturnType<FilestackClient['upload']> {
  return uploadWithFilestackSecurityRetry(
    filestackIntent,
    security => {
      const client = filestack.init(FILESTACK_API_KEY, { security });
      return client.upload(file, uploadOptions, storeOptions, undefined, security);
    },
    file.size
  );
}
